import { Box, Dialog, DialogContent, FormControl, FormHelperText, GlobalStyles, InputLabel } from "@mui/material";
import { Editor, IAllProps } from "@tinymce/tinymce-react";
import RegisterPlugins from "components/TinyMCEPlugins/Plugins/RegisterPlugins";
import { MEDIA_LIBRARY_OPEN_EVENT } from "constants/TinyMCEEventConstants";
import { MediaLibraryPluginView } from "components/TinyMCEPlugins/Plugins/MediaLibraryPlugin/MediaLibraryPluginView";
import "components/TinymceSetup";
import { BASE_PLUGINS, TOOLBAR_RICH, TOOLBAR_RICH_WITH_TEMPLATES, TINYMCE_GLOBAL_STYLES, registerToolbarOverflowClickOutside } from "./toolbarPresets";
import { Editor as TinyMCEEditor } from "tinymce/tinymce";
import { useCallback, useState } from "react";
import { FieldInputProps, FieldMetaState } from "react-final-form";
import { useIntl } from "react-intl";
import { ReplacementText } from "types/contentTemplateTypes";

export const removeClasses = (_: any, args: any) => {
  const div = document.createElement("div");
  div.innerHTML = args.content;
  div.querySelectorAll("[class]").forEach((el) => el.removeAttribute("class"));
  div.querySelectorAll("[id]").forEach((el) => el.removeAttribute("id"));
  args.content = div.innerHTML;
};

const styles = {
  label: { position: "relative", transform: "none" },
  editorBox: { mt: 1 },
} as const;

type RichTextEditorProps = {
  input: FieldInputProps<any, HTMLElement>;
  meta: FieldMetaState<string>;
  label?: React.ReactNode;
  oneLineMode?: "false" | "true";
  setupEditor?: (e: TinyMCEEditor) => void;
  cleanupClasses?: boolean;
  variant?: "standard" | "outlined" | "filled";
  required?: boolean;
  replacementText?: ReplacementText[];
} & Partial<IAllProps>;


const RichTextEditor = ({
  input,
  label,
  meta,
  oneLineMode,
  setupEditor,
  cleanupClasses = true,
  variant,
  required = false,
  replacementText,
  onInit,
  init = {},
  ...rest
}: RichTextEditorProps) => {
  const [value, setValue] = useState(input.value);
  const intl = useIntl();

  const hasReplacements = !!replacementText && replacementText.length > 0;

  /* eslint-disable react-hooks/exhaustive-deps */
  const onBlur = useCallback(() => input.onBlur(), [input.onBlur]);
  const onFocus = useCallback(() => input.onFocus(), [input.onFocus]);

  const [isReady, setIsReady] = useState(false);

  const [mediaLibraryEditor, setMediaLibraryEditor] = useState<TinyMCEEditor | null>(null);
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);

  const closeMediaLibrary = useCallback(() => setIsMediaLibraryOpen(false), []);

  const handleInit = useCallback<NonNullable<IAllProps["onInit"]>>(
    (event, editor) => {
      editor.on(MEDIA_LIBRARY_OPEN_EVENT, () => {
        setMediaLibraryEditor(editor);
        setIsMediaLibraryOpen(true);
      });
      setIsReady(true);
      onInit?.(event, editor);
    },
    [onInit]
  );

  const onEditorChange = (v: string) => {
    // Ideally we'd let react-final-form control this input, however for some incomprehensible
    // reason, which seems to be related to string references, it fails to work correctly for
    // editors that are created dynamically (such as when adding a new question).
    // Hours of trying to solve this resulted in this solution - we have a controlled component
    // with the state store locally, and we call onChange jsut to let the form know about the update.

    let next = v;
    if (hasReplacements && next) {
      replacementText.forEach((el) => {
        next = next.replaceAll(el.textToReplace, el.textReplaceWith);
      });
    }

    setValue(next);
    input.onChange(next);
  };

  const setup = useCallback((editor: TinyMCEEditor) => {
    RegisterPlugins({ editor, intl, ...rest });
    setupEditor && setupEditor(editor);
    // eslint-disable-next-line eqeqeq
    if (oneLineMode == "true") {
      editor.on("keydown", (e) => {
        // eslint-disable-next-line eqeqeq
        if (e.code == "Enter") {
          e.preventDefault();
        }
      });
      editor.on("PastePreProcess", (data) => {
        data.content = data.content.replace(/(<br\s*\/?>|<p\s*\/?>)/gm, "");
      });
    }
    registerToolbarOverflowClickOutside(editor);
  }, []);

  return (
    <>
    <GlobalStyles styles={TINYMCE_GLOBAL_STYLES} />
    <FormControl
      variant={variant}
      fullWidth
      error={meta.error && meta.submitFailed}
    >
      {label !== undefined && (
        <InputLabel shrink required={required} sx={styles.label}>
          {label}
        </InputLabel>
      )}
      <Box sx={styles.editorBox}>
        <Editor
          value={value}
          // The handover. "readonly" below is a creation setting the editor reads once and never
          // again, so this is the only way to let go of it afterwards: changing it switches the
          // editor's mode.
          disabled={!isReady}
          onEditorChange={onEditorChange}
          onSelectionChange={() => false}
          onBlur={onBlur}
          onFocus={onFocus}
          onInit={handleInit}
          init={{
            // The toolbar is drawn before the editor can accept input, and anything entered in
            // that window is discarded when it finishes starting up - silently, with nothing to
            // undo. Born read only, the editor never makes its writing surface editable in that
            // window; the prop above hands it over once the editor reports it has started.
            readonly: true,
            branding: false,
            skin: "oxide",
            content_css: false,
            height: 200,
            menubar: false,
            forced_root_block: false,
            paste_preprocess: cleanupClasses ? removeClasses : undefined,
            valid_children:
              "+body[style],+div[style],+td[style],+table[style],+tr[style],+span[style],+p[style]",
            plugins: BASE_PLUGINS,
            statusbar: false,
            toolbar: hasReplacements ? TOOLBAR_RICH_WITH_TEMPLATES : TOOLBAR_RICH,
            toolbar_mode: "wrap",
            toolbar_sticky: true,
            table_toolbar: false,
            setup,
            ...init,
          }}
          {...rest}
        />
      </Box>
      {meta.error && meta.submitFailed && (
        <FormHelperText>{meta.error}</FormHelperText>
      )}
    </FormControl>
    <Dialog
      fullWidth
      maxWidth="lg"
      open={isMediaLibraryOpen}
      onClose={closeMediaLibrary}
    >
      <DialogContent>
        {mediaLibraryEditor !== null && (
          <MediaLibraryPluginView
            editor={mediaLibraryEditor}
            onClose={closeMediaLibrary}
          />
        )}
      </DialogContent>
    </Dialog>
    </>
  );
};

export default RichTextEditor;
